import classnames from 'classnames/bind';

import Application from 'types/application';
import { NotificationChannelType } from 'types/notification_channel';

import { useGroupedApplications } from 'application/hooks/useGroupedApplications';
import { useNotificationChannelPreferences } from 'settings/hooks/useNotificationChannelPreferences';
import { useUpsertNotificationChannelPreference } from 'settings/hooks/useUpsertNotificationChannelPreference';

import * as PageLayout from 'components/PageLayout';
import { Box } from 'components/Box/Box';
import { Heading } from 'components/Heading/Heading';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { Loader } from 'components/Loader/Loader';
import { Text } from 'components/Text/Text';

import styles from '../Settings.module.scss';

const c = classnames.bind(styles);

const CHANNEL_LABELS: Record<
  NotificationChannelType,
  { label: string; description: string }
> = {
  email: {
    label: 'Email',
    description: 'Receive notifications via email',
  },
  push: {
    label: 'Push notifications',
    description: 'Receive push notifications',
  },
  sms: {
    label: 'SMS',
    description: 'Receive notifications via text message',
  },
};

const CHANNEL_TYPES: NotificationChannelType[] = ['email', 'push'];

export function NotificationChannels() {
  const groupedApplications = useGroupedApplications();

  if (groupedApplications.length === 0) {
    return <Loader text="Loading notification preferences..." />;
  }

  return (
    <PageLayout.Root size="small">
      <PageLayout.Container>
        <div className={c('wrap')}>
          <Heading level="2">Notification preferences</Heading>
          <Text>
            Choose how you want to receive notifications for each workspace.
          </Text>

          {groupedApplications.map((group) => (
            <section key={group.id} className="flex flex-col gap-3">
              <Heading level="3">{group.name}</Heading>
              {group.applications.map((app) => (
                <ApplicationChannelPreferences
                  key={app.id}
                  application={app}
                />
              ))}
            </section>
          ))}
        </div>
      </PageLayout.Container>
    </PageLayout.Root>
  );
}

type ApplicationChannelPreferencesProps = {
  application: Application;
};

function ApplicationChannelPreferences({
  application,
}: ApplicationChannelPreferencesProps) {
  const { data: preferences, isLoading } = useNotificationChannelPreferences({
    applicationId: application.id,
  });
  const { mutate: upsert } = useUpsertNotificationChannelPreference();

  function isChannelEnabled(channelType: NotificationChannelType) {
    const pref = preferences?.find((p) => p.channel_type === channelType);
    return pref?.enabled ?? false;
  }

  function handleToggle(
    channelType: NotificationChannelType,
    enabled: boolean
  ) {
    upsert({ applicationId: application.id, channel_type: channelType, enabled });
  }

  return (
    <Box variant="secondary">
      <Heading level="4">{application.name}</Heading>
      {isLoading ? (
        <Loader size="small" text="Loading..." />
      ) : (
        CHANNEL_TYPES.map((channelType) => {
          const { label, description } = CHANNEL_LABELS[channelType];

          return (
            <div key={channelType} className={c('toggle-field')}>
              <LightSwitch
                checked={isChannelEnabled(channelType)}
                onValueChange={(checked) => handleToggle(channelType, checked)}
              />
              <div>
                <Text>
                  <strong>{label}</strong>
                </Text>
                <Text size="small">{description}</Text>
              </div>
            </div>
          );
        })
      )}
    </Box>
  );
}
